import React from 'react';
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

const HeroSection = () => {
    const { t } = useTranslation();

    return (
        <section
            className="relative min-h-screen flex flex-col justify-center overflow-hidden"
            id="home"
            style={{ background: 'linear-gradient(135deg, #010C66 0%, #0a1580 60%, #010C66 100%)' }}
        >
            {/* Abstract mesh gradient shapes */}
            <div
                className="absolute top-0 right-0 w-[55%] h-full opacity-30"
                style={{
                    background: 'radial-gradient(ellipse at 80% 40%, #709CFF 0%, transparent 60%), radial-gradient(ellipse at 60% 80%, #4338ca 0%, transparent 50%)',
                }}
            />
            <div
                className="absolute bottom-0 left-0 w-[40%] h-[50%] opacity-20"
                style={{
                    background: 'radial-gradient(ellipse at 20% 80%, #818cf8 0%, transparent 60%)',
                }}
            />

            {/* Subtle grid */}
            <div
                className="absolute inset-0 opacity-[0.04]"
                style={{
                    backgroundImage: 'linear-gradient(rgba(255,255,255,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.5) 1px, transparent 1px)',
                    backgroundSize: '80px 80px',
                }}
            />

            <div className="container relative z-10 py-32 lg:py-40">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                    {/* Left: Text content */}
                    <div className="flex flex-col gap-6">
                        <div
                            className="inline-flex items-center gap-2 self-start text-xs font-semibold rounded-full px-3 py-1.5"
                            style={{ background: 'rgba(0,201,177,0.12)', color: '#00C9B1', border: '1px solid rgba(0,201,177,0.25)' }}
                        >
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
                                <path d="M20 6L9 17l-5-5" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                            Microsoft for Startups Partner
                        </div>

                        <h1
                            className="text-white leading-tight"
                            style={{
                                fontFamily: '"Wix Madefor Display", sans-serif',
                                fontSize: 'clamp(2.5rem, 5vw, 4rem)',
                                fontWeight: 800,
                                lineHeight: 1.1,
                            }}
                        >
                            {t("Index.title")}
                        </h1>

                        <p
                            className="text-lg max-w-lg"
                            style={{ color: 'rgba(255,255,255,0.65)', lineHeight: 1.7 }}
                        >
                            {t("Index.subtext")}
                        </p>

                        <div className="flex flex-col sm:flex-row gap-4 mt-2">
                            <Link
                                to="/#contact"
                                className="inline-flex items-center justify-center px-7 py-3.5 rounded-lg font-semibold text-base transition-all duration-300 hover:opacity-90"
                                style={{
                                    background: '#00C9B1',
                                    color: '#010C66',
                                    fontFamily: '"Inter", sans-serif',
                                    boxShadow: '0 0 24px rgba(0,201,177,0.45)',
                                }}
                            >
                                {t("CTA.requestDemo")}
                            </Link>
                            <Link
                                to="/#how-it-works"
                                className="inline-flex items-center justify-center px-7 py-3.5 rounded-lg font-semibold text-base transition-all duration-300"
                                style={{
                                    border: '1.5px solid rgba(255,255,255,0.25)',
                                    color: 'rgba(255,255,255,0.85)',
                                    fontFamily: '"Inter", sans-serif',
                                }}
                            >
                                {t("CTA.seeHowItWorks")}
                            </Link>
                        </div>
                    </div>

                    {/* Right: Abstract visual element */}
                    <div className="hidden lg:flex items-center justify-center relative">
                        <div
                            className="w-[420px] h-[420px] rounded-3xl relative overflow-hidden"
                            style={{
                                background: 'linear-gradient(135deg, rgba(112,156,255,0.15) 0%, rgba(67,56,202,0.2) 100%)',
                                border: '1px solid rgba(112,156,255,0.25)',
                                backdropFilter: 'blur(10px)',
                            }}
                        >
                            {/* Inner glow orbs */}
                            <div
                                className="absolute top-8 right-8 w-48 h-48 rounded-full"
                                style={{ background: 'radial-gradient(circle, rgba(112,156,255,0.4) 0%, transparent 70%)' }}
                            />
                            <div
                                className="absolute bottom-8 left-8 w-32 h-32 rounded-full"
                                style={{ background: 'radial-gradient(circle, rgba(99,102,241,0.5) 0%, transparent 70%)' }}
                            />

                            {/* Floating stat cards */}
                            <div className="absolute inset-0 flex flex-col items-center justify-center gap-4 p-8">
                                <div
                                    className="w-full rounded-2xl p-4 flex items-center gap-4"
                                    style={{ background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.12)' }}
                                >
                                    <div
                                        className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
                                        style={{ background: 'rgba(112,156,255,0.3)' }}
                                    >
                                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                                            <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5" stroke="#709CFF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                        </svg>
                                    </div>
                                    <div>
                                        <p className="text-white font-semibold text-sm" style={{ fontFamily: '"Wix Madefor Display", sans-serif' }}>{t("Index.card1Title")}</p>
                                        <p className="text-xs" style={{ color: 'rgba(255,255,255,0.5)' }}>{t("Index.card1Desc")}</p>
                                    </div>
                                </div>
                                <div
                                    className="w-full rounded-2xl p-4 flex items-center gap-4"
                                    style={{ background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.12)' }}
                                >
                                    <div
                                        className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
                                        style={{ background: 'rgba(99,102,241,0.3)' }}
                                    >
                                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                                            <path d="M9 3H5a2 2 0 00-2 2v4m6-6h10a2 2 0 012 2v4M9 3v18m0 0h10a2 2 0 002-2V9M9 21H5a2 2 0 01-2-2V9m0 0h18" stroke="#818cf8" strokeWidth="2" strokeLinecap="round"/>
                                        </svg>
                                    </div>
                                    <div>
                                        <p className="text-white font-semibold text-sm" style={{ fontFamily: '"Wix Madefor Display", sans-serif' }}>{t("Index.card2Title")}</p>
                                        <p className="text-xs" style={{ color: 'rgba(255,255,255,0.5)' }}>{t("Index.card2Desc")}</p>
                                    </div>
                                </div>
                                <div
                                    className="w-full rounded-2xl p-4 flex items-center gap-4"
                                    style={{ background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.12)' }}
                                >
                                    <div
                                        className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
                                        style={{ background: 'rgba(67,56,202,0.3)' }}
                                    >
                                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                                            <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" stroke="#a5b4fc" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                        </svg>
                                    </div>
                                    <div>
                                        <p className="text-white font-semibold text-sm" style={{ fontFamily: '"Wix Madefor Display", sans-serif' }}>{t("Index.card3Title")}</p>
                                        <p className="text-xs" style={{ color: 'rgba(255,255,255,0.5)' }}>{t("Index.card3Desc")}</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default HeroSection;
